import {skipToken} from '@reduxjs/toolkit/query'
import {useCallback, useEffect, useMemo} from 'react'
import type {EntryPointProps} from 'react-relay'
import {usePreloadedQuery} from 'react-relay'
import {createSelector} from 'reselect'

import type {projectTreesFederationServersFragment$key} from '../../../hooks/__generated__/projectTreesFederationServersFragment.graphql'
import {
  useDefaultSearchProjectsTree,
  useFederationServers,
  useHasArchivedDescendants,
} from '../../../hooks/projectTrees'
import useProjectsSearch from '../../../hooks/useProjectsSearch'
import type {State} from '../../../reducers/types'
import {archivedProjectsOptions, getProjectsArg, getSingleProjectArg} from '../../../rest/projects'
import {getClassicOverviewHrefWithCustomBase} from '../../../routes'
import {
  getOverviewData,
  getOverviewProjectsHash,
  isArchivedProject,
  isProjectLoaded,
} from '../../../selectors'
import {
  createSearchProjectsTree,
  getProjectsAndOverviewReady,
  getFederationServersData,
  mergeSearchProjectsTree,
  upgradeSearchProjectsTreeByExpandState,
} from '../../../selectors/projectTrees'
import {restApi} from '../../../services/rest'
import type {ProjectId, ProjectOrBuildTypeNode} from '../../../types'
import {ROOT_PROJECT_ID} from '../../../types'
import {noop} from '../../../utils/empty'
import ProjectsTree from '../ProjectsTree/ProjectsTree'
import {calculateRowOffset, getIdProps} from '../ProjectsTree/ProjectsTree.utils'
import {getCustomUrl} from '../ProjectsTree/ProjectsTreeItem/ProjectsTreeItem'
import ProjectTreeSearch from '../ProjectsTree/ProjectTreeSearch/ProjectTreeSearch'
import SidebarFooter from '../SidebarFooter/SidebarFooter'
import {fetchProjectsForAllFederationServers} from '../SidebarFooter/SidebarFooter.actions'

import type {ProjectsPopupFederationServersQuery} from './__generated__/ProjectsPopupFederationServersQuery.graphql'
import {ProjectsPopupContext} from './ProjectsPopup.context'
import {federationServersQuery} from './ProjectsPopup.queries'

import styles from './ProjectsPopup.css'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'

const SEARCH_HEIGHT = 50
const FOOTER_HEIGHT = 41

export type ProjectsPopupProps = {
  parentProjectId?: ProjectId
  editMode?: boolean
  activeItem?: ProjectOrBuildTypeNode | null | undefined
  buildTypeUrlFormat?: string | null | undefined
  templateUrlFormat?: string | null | undefined
  projectUrlFormat?: string | null | undefined
}

type InnerProps = ProjectsPopupProps & {
  federationServersKey: projectTreesFederationServersFragment$key | null
}

export function ProjectsPopup({
  parentProjectId,
  editMode = false,
  activeItem,
  buildTypeUrlFormat,
  templateUrlFormat,
  projectUrlFormat,
  federationServersKey,
}: InnerProps) {
  const federationServers = useFederationServers(federationServersKey)
  const dispatch = useAppDispatch()
  const withArchived = useAppSelector(state => state.projectsSidebar.showArchivedProjects)
  useEffect(() => {
    dispatch(fetchProjectsForAllFederationServers(federationServers, withArchived))
  }, [dispatch, federationServers, withArchived])

  const [{searchQuery, expandState}, {setSearchQuery, toggleExpandState}] = useProjectsSearch()
  const filter = useMemo(
    () => ({
      parentId: parentProjectId,
      includeTemplates: editMode,
    }),
    [editMode, parentProjectId],
  )

  const defaultTree = useDefaultSearchProjectsTree(federationServersKey, filter)

  const getDefaultSearchItems = useMemo(
    () =>
      createSelector(
        (state: State) => state.togglingOverview,
        getOverviewData,
        getOverviewProjectsHash,
        (togglingOverview, overviewData, overviewProjectsHash) =>
          createSearchProjectsTree({
            searchQuery,
            defaultTreeByServer: defaultTree,
            togglingOverview,
            overviewData,
            overviewProjectsHash,
            federationServers,
            parentId: parentProjectId,
          }),
      ),
    [searchQuery, defaultTree, federationServers, parentProjectId],
  )
  const getItems = useMemo(
    () =>
      createSelector(
        getDefaultSearchItems,
        getFederationServersData,
        (defaultSearchItems, federationServersData) =>
          mergeSearchProjectsTree(
            upgradeSearchProjectsTreeByExpandState(
              [],
              defaultSearchItems,
              expandState,
              !!searchQuery,
            ),
            federationServers,
            federationServersData,
          ).data,
      ),
    [getDefaultSearchItems, expandState, searchQuery, federationServers],
  )
  const items = useAppSelector(getItems)
  const isParentLoaded = useAppSelector(state => isProjectLoaded(state, parentProjectId))
  const ready = useAppSelector(
    state =>
      getProjectsAndOverviewReady(state, parentProjectId) &&
      (parentProjectId == null || isParentLoaded),
  )
  restApi.endpoints.getProjectNormalized.useQuerySubscription(
    parentProjectId != null && !isParentLoaded ? getSingleProjectArg(parentProjectId) : skipToken,
  )
  restApi.endpoints.getAllProjectsNormalized.useQuerySubscription(
    getProjectsArg(parentProjectId ?? ROOT_PROJECT_ID, archivedProjectsOptions),
  )
  const onChangeSearchActive = useCallback(
    (active: boolean) => {
      if (!active) {
        setSearchQuery('')
      }
    },
    [setSearchQuery],
  )
  const hasArchivedDescendants = useHasArchivedDescendants(filter)
  const showArchivedSwitch = useAppSelector(
    state => !isArchivedProject(state, parentProjectId) && hasArchivedDescendants,
  )
  const contextValue = useMemo(
    () => ({
      isPopup: true,
      isAdmin: editMode,
      buildTypeUrlFormat,
      templateUrlFormat,
      projectUrlFormat,
    }),
    [buildTypeUrlFormat, templateUrlFormat, editMode, projectUrlFormat],
  )
  return (
    <ProjectsPopupContext.Provider value={contextValue}>
      <div
        className={styles.container}
        style={{
          height: SEARCH_HEIGHT + calculateRowOffset(items.length, items) + FOOTER_HEIGHT,
        }}
      >
        <ProjectsTree
          activeItem={activeItem}
          className={styles.tree}
          contentClassName={styles.treeContent}
          dispatch={dispatch}
          isPopup
          ready={ready}
          items={items}
          placeholder={<span className={styles.placeholder}>{'Nothing found'}</span>}
          toggleExpandState={(projectId, expanded) =>
            toggleExpandState({
              projectId,
              expanded,
            })
          }
          toggleExpandAll={noop}
          searchActive
          searchQuery={searchQuery}
          renderSearch={({sidebarRef, collapsed, ...searchProps}) => (
            <ProjectTreeSearch
              {...searchProps}
              className={styles.search}
              searchQuery={searchQuery}
              onChangeQuery={setSearchQuery}
              onChangeActive={onChangeSearchActive}
              autoFocus
              propagateEscape
            />
          )}
          changeActiveItem={item => {
            const {itemType, serverId} = item
            const idProps = getIdProps(item)
            location.href =
              getCustomUrl({
                ...idProps,
                itemType,
                buildTypeUrlFormat,
                templateUrlFormat,
                projectUrlFormat,
              }) ?? getClassicOverviewHrefWithCustomBase({...idProps, serverId, isAdmin: editMode})
          }}
        >
          {showArchivedSwitch && <SidebarFooter />}
        </ProjectsTree>
      </div>
    </ProjectsPopupContext.Provider>
  )
}

type Queries = {federationServers: ProjectsPopupFederationServersQuery}

export default function ProjectPopupContainer({
  queries,
  props,
}: EntryPointProps<Queries, {}, ProjectsPopupProps, {}>) {
  const {federationServers} = usePreloadedQuery(federationServersQuery, queries.federationServers)
  const isBreadcrumbs = props.parentProjectId != null
  return (
    <ProjectsPopup
      {...props}
      federationServersKey={isBreadcrumbs ? null : federationServers ?? null}
    />
  )
}
export type ProjectPopupContainerComponent = typeof ProjectPopupContainer
